import { useTranslation } from 'react-i18next'
import { Box, Divider, FormControlLabel, IconButton, Checkbox as MUICheckbox, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { TextField } from '@carsys/form'
import { useFormikContext } from 'formik'
import { useEffect, useMemo, useRef } from 'react'
import {
  AddToOrderMenu,
  DashboardCatalogSectionKey,
  ExternalPortal,
  IFormValues,
  OrderInPreparation,
  QuickNavigationProductGroup
} from './types.d'
import { useFeature } from '@cbgms/base/feature-flags'
import { useGetExternalResources } from 'app-support/wholesale/hooks/useGetExternalResources'
import { hasVPOMFields } from './CatalogFieldsVPOM'
import { useParams } from 'react-router-dom'
import { useGetWholesalerWithServices } from 'app-support/wholesale/hooks/useGetWholesalerWithServices'
import { useFilteredExternalResources } from 'app-support/wholesale/hooks/useFilteredExternalResources'
import { useGetWholesalerCatalog } from 'app-support/wholesale/hooks/useGetWholesalerCatalog'

const dashboardCatalogInfoName = 'DashboardCatalogInfo'
const oldStructureVPOMUUIDKey = 'VPOMDashboardCatalogUUIDS'

const updateSelection = (
  portals: ExternalPortal[] | OrderInPreparation[] | AddToOrderMenu[] | QuickNavigationProductGroup[],
  selected: ExternalPortal[] | OrderInPreparation[] | AddToOrderMenu[] | QuickNavigationProductGroup[]
) => {
  const selectedMap = new Map(selected.map(p => [p.UniqueKey, p.Selected]))
  return portals.map(p => ({
    ...p,
    Selected: selectedMap.get(p.UniqueKey) ?? false
  }))
}

const CatalogCheckboxGroup = ({
  label,
  sectionKey,
  portals
}: {
  label: string
  sectionKey: DashboardCatalogSectionKey
  portals: ExternalPortal[] | OrderInPreparation[] | AddToOrderMenu[] | QuickNavigationProductGroup[]
}) => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext<IFormValues>()
  const selected = values?.DashboardCatalogInfo?.[sectionKey] || []

  const updatedList = useMemo(() => updateSelection(portals, selected), [portals, selected])

  const onChange = (portal: ExternalPortal | OrderInPreparation | AddToOrderMenu | QuickNavigationProductGroup) => {
    const isAlreadySelected = selected.some(
      (p: ExternalPortal | OrderInPreparation | AddToOrderMenu | QuickNavigationProductGroup) =>
        p.UniqueKey === portal.UniqueKey && portal.Selected
    )
    const newList = isAlreadySelected
      ? selected.filter(
          (p: ExternalPortal | OrderInPreparation | AddToOrderMenu | QuickNavigationProductGroup) => p.UniqueKey !== portal.UniqueKey
        )
      : [...selected, { ...portal, Selected: true }]

    setFieldValue(`${dashboardCatalogInfoName}.${sectionKey}`, newList)
  }

  return (
    <Box sx={{ mb: 3 }}>
      {label === DashboardCatalogSectionKey.OrderInPreparation || label === DashboardCatalogSectionKey.AddToOrderMenu ? (
        <Typography variant='h6' sx={{ mb: 1 }}>
          {t(`wholesaler-catalog.${label}`)}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {updatedList.map(p => (
          <FormControlLabel key={p.UniqueKey} control={<MUICheckbox checked={p.Selected} onChange={() => onChange(p)} />} label={p.Name} />
        ))}
      </Box>
    </Box>
  )
}

export const DashboardCatalogAndExternalResources = () => {
  const { t } = useTranslation()
  const hasExternalCatalogsFeature = useFeature('external-catalogs')
  const { wholesaleUUID } = useParams<{ wholesaleUUID: string }>()
  const { data: wholesalerBaseInfo, isLoading: isLoadingWholesalerBaseInfo } = useGetWholesalerWithServices(wholesaleUUID)
  const { data: externalResources } = useGetExternalResources()
  const { data: wholesalerCatalog, isLoading: isLoadingwholesalerCatalog } = useGetWholesalerCatalog(wholesaleUUID)
  const { values, setFieldValue } = useFormikContext<IFormValues>()
  const didInitDefaults = useRef(false)
  const { wholesaleCatalogType } = values
  const vpomGroups = values?.[dashboardCatalogInfoName]?.Groups || []

  const renderVPOMProductTreeInput = useMemo(() => hasVPOMFields(wholesaleCatalogType), [wholesaleCatalogType])

  const { filteredExternalPortals, filteredOrderInPreparation, filteredAddToOrderMenu } = useFilteredExternalResources(
    externalResources,
    wholesalerBaseInfo
  )

  useEffect(() => {
    if (didInitDefaults.current) return

    const needsInit = !wholesalerCatalog?.length && (filteredExternalPortals || filteredOrderInPreparation || filteredAddToOrderMenu)

    if (needsInit && !isLoadingwholesalerCatalog) {
      setFieldValue(`${dashboardCatalogInfoName}.${DashboardCatalogSectionKey.ExternalPortals}`, filteredExternalPortals || [])
      setFieldValue(`${dashboardCatalogInfoName}.${DashboardCatalogSectionKey.OrderInPreparation}`, filteredOrderInPreparation || [])
      setFieldValue(`${dashboardCatalogInfoName}.${DashboardCatalogSectionKey.AddToOrderMenu}`, filteredAddToOrderMenu || [])
      didInitDefaults.current = true
    }
  }, [
    values,
    setFieldValue,
    wholesalerCatalog,
    isLoadingwholesalerCatalog,
    filteredExternalPortals,
    filteredOrderInPreparation,
    filteredAddToOrderMenu
  ])

  const push = () => {
    const newItem = { TreeUUID: '' }
    setFieldValue(`${dashboardCatalogInfoName}.Groups`, [...vpomGroups, newItem])
    setFieldValue(oldStructureVPOMUUIDKey, [...(values?.[oldStructureVPOMUUIDKey] || []), newItem])
  }

  const remove = (index: number) => {
    const updatedGroups = [...vpomGroups]
    const updatedOld = [...(values?.[oldStructureVPOMUUIDKey] || [])]
    updatedGroups.splice(index, 1)
    updatedOld.splice(index, 1)
    setFieldValue(`${dashboardCatalogInfoName}.Groups`, updatedGroups)
    setFieldValue(oldStructureVPOMUUIDKey, updatedOld)
  }

  useEffect(() => {
    setFieldValue(oldStructureVPOMUUIDKey, vpomGroups)
  }, [vpomGroups])

  if (isLoadingWholesalerBaseInfo || !wholesalerBaseInfo) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {renderVPOMProductTreeInput || hasExternalCatalogsFeature ? (
          <Typography variant='h6'>{t('wholesaler-catalog.VPOMDashboardCatalogUUIDS')}</Typography>
        ) : null}

        {renderVPOMProductTreeInput ? (
          <IconButton onClick={push} size='large'>
            <AddIcon />
          </IconButton>
        ) : null}
      </Box>

      {/* Dashboard Catalogs */}
      {hasExternalCatalogsFeature && (
        <CatalogCheckboxGroup
          label={oldStructureVPOMUUIDKey}
          sectionKey={DashboardCatalogSectionKey.ExternalPortals}
          portals={externalResources?.ExternalPortal || []}
        />
      )}

      {renderVPOMProductTreeInput ? (
        <Box sx={{ mb: 3 }}>
          {vpomGroups.map((_, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField name={`${dashboardCatalogInfoName}.Groups[${i}].TreeUUID`} label={t('wholesaler-catalog.VPOMProductTreeID')} />
              <IconButton onClick={() => remove(i)} size='large'>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : null}

      {hasExternalCatalogsFeature ? (
        <>
          <Divider sx={{ my: 4 }} />

          <CatalogCheckboxGroup
            label={DashboardCatalogSectionKey.OrderInPreparation}
            sectionKey={DashboardCatalogSectionKey.OrderInPreparation}
            portals={externalResources?.OrderInPreparation || []}
          />

          <Divider sx={{ my: 4 }} />

          <CatalogCheckboxGroup
            label={DashboardCatalogSectionKey.AddToOrderMenu}
            sectionKey={DashboardCatalogSectionKey.AddToOrderMenu}
            portals={externalResources?.AddToOrderMenu || []}
          />
          <Divider sx={{ my: 4 }} />
        </>
      ) : (
        <Divider sx={{ my: 4 }} />
      )}
    </Box>
  )
}
